import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Badge,
  Tabs,
  Tab,
  Avatar,
  Paper,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextNew';
import SignUpPromptModal from './SignUpPromptModal';
import useResponsive from '../hooks/useResponsive';
import BottomNavigation from './BottomNavigation';

const Cart = ({ open, onClose }) => {
  const { 
    items, 
    totalItems, 
    loading,
    error,
    removeFromCart, 
    updateQuantity, 
    getCartSummary,
    clearProviderCart 
  } = useCart();
  
  const { isGuest } = useAuth();
  const { isMobile } = useResponsive();
  
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [restrictedFeature, setRestrictedFeature] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  

  const cartSummary = getCartSummary();
  const providers = cartSummary.map(summary => summary.providerId);
  
  // Reset selectedProvider if it no longer exists in the cart
  useEffect(() => {
    if (selectedProvider && !providers.includes(selectedProvider)) {
    //  console.log('🔍 Resetting selectedProvider because provider no longer exists:', selectedProvider);
      setSelectedProvider(null);
      setSelectedTab(0);
    }
    // If cart is empty, reset selectedProvider
    if (providers.length === 0) {
   //   console.log('🔍 Resetting selectedProvider because cart is empty');
      setSelectedProvider(null);
      setSelectedTab(0);
    }
  }, [selectedProvider, providers]);
  

  // Guest mode handlers
  const handleGuestRestriction = (featureName) => {
    setRestrictedFeature(featureName);
    setShowSignUpPrompt(true);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setSelectedProvider(providers[newValue] || null);
  };

  const handleQuantityChange = (item, newQuantity) => {
    updateQuantity(item.cartItemId || item.id, newQuantity);
  };

  const handleRemoveItem = (item) => {
    removeFromCart(item.cartItemId || item.id);
  };

  const handleCheckout = (providerId) => {
    if (isGuest) {
      handleGuestRestriction('checkout');
      return;
    }
    
  //  console.log(`Checkout for provider: ${providerId}`);
    
    // Get provider details and cart items for this provider
    const providerItems = items.filter(item => item.providerId === providerId);
    
    // Check if all items are products (no services)
    const hasOnlyProducts = providerItems.every(item => item.itemType === 'product');
    const hasOnlyServices = providerItems.every(item => item.itemType === 'service');
    
    // Create provider object
    const provider = {
      id: providerId,
      name: getProviderName(providerId),
      type: providerId === 'store' ? 'store' : 'provider'
    };
    
    if (hasOnlyProducts) {
      // Navigate to product checkout
      navigate('/product-checkout', {
        state: {
          provider,
          cartItems: providerItems,
          fromCart: true
        }
      });
    } else if (hasOnlyServices) {
      // Navigate to availability page for services
      navigate(`/availability/${providerId}`, {
        state: {
          provider,
          items: providerItems,
          fromCart: true
        }
      });
    } else {
      // Mixed cart - show error or handle appropriately
      console.error('Mixed cart not supported');
    }
    
    onClose();
  };

  // Helper functions
  const getProviderName = (providerId) => {
    switch (providerId) {
      case 'store':
        return 'NISTYLEZ Store';
      case 'salon':
        return 'Salon';
      case 'pros':
        return 'Professional';
      default:
        // Try to get provider name from cart items
        const providerItem = items.find(item => item.providerId === providerId);
        if (providerItem && providerItem.providerName) {
          return providerItem.providerName;
        }
        // Fallback to provider ID or a default name
        return providerId || 'Provider';
    }
  };

  const getProviderIcon = (providerId) => {
    switch (providerId) {
      case 'store':
        return <StoreIcon />;
      case 'salon':
        return <StoreIcon />;
      case 'pros':
        return <PersonIcon />;
      default:
        return <StoreIcon />;
    }
  };

  const formatPrice = (price) => {
    if (typeof price === 'string') {
      return price;
    }
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const formatDuration = (duration) => {
    if (!duration || duration === 'undefined' || duration === 'null') return 'N/A';
    
    // If duration is already a string with "min" or similar, return as is
    if (typeof duration === 'string') {
      if (duration.includes('min') || duration.includes('hour') || duration.includes('hr')) {
        return duration;
      }
      // If it's just a number as string, add "min"
      const numDuration = parseInt(duration);
      if (!isNaN(numDuration)) {
        return `${numDuration} min`;
      }
      return duration;
    }
    
    // If duration is a number, add "min"
    if (typeof duration === 'number') {
      return `${duration} min`;
    }
    
    return 'N/A';
  };

  const handleClearProvider = (providerId) => {
    clearProviderCart(providerId);
  };

  const renderProviderItems = (providerId) => {
    const summary = cartSummary.find(summary => summary.providerId === providerId);
    
    return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {summary?.products.map((item) => (
                    <Card key={`${item.id}-${item.itemType}-${item.providerId}`} sx={{
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      border: '1px solid #f1f3f4',
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                        borderColor: '#0A2A66'
                      }
                    }}>
                      <CardContent sx={{ p: isMobile ? 1.5 : 2.5 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: isMobile ? 1.5 : 2.5,
                          flexDirection: isMobile ? 'column' : 'row'
                        }}>
                          <CardMedia
                            component="img"
                            sx={{ 
                              width: isMobile ? 60 : 80, 
                              height: isMobile ? 60 : 80, 
                              borderRadius: 2,
                              objectFit: 'cover',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              border: '1px solid #f8f9fa',
                              alignSelf: isMobile ? 'center' : 'flex-start'
                            }}
                            image={item.image}
                            alt={item.name}
                          />
                          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography variant="subtitle1" sx={{ 
                                fontSize: '1rem',
                                fontWeight: 600,
                                color: '#212529',
                                mb: 0.5,
                                lineHeight: 1.2
                              }}>
                                {item.productName || item.name}
                            </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{
                                fontSize: '0.75rem',
                                color: '#666',
                                fontWeight: 500,
                                mb: 0.5
                              }}>
                              by {item.brand || item.providerName || 'NISTYLEZ Store'}
                            </Typography>
                              <Chip 
                                label="Product" 
                                size="small" 
                                sx={{ 
                                  background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                                  color: '#0A2A66',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: 20
                                }} 
                              />
                            </Box>
                            <Typography variant="h6" color="primary" sx={{ 
                              mt: 1,
                              fontWeight: 700,
                              fontSize: '1.1rem',
                              color: '#0A2A66'
                            }}>
                              {formatPrice(item.price)}
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: 1.5,
                            justifyContent: 'space-between',
                            minWidth: 80
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                              borderRadius: 2,
                              p: 1,
                              border: '1px solid #dee2e6'
                            }}>
                              <IconButton
                                size="small"
                                onClick={() => handleQuantityChange(item, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                                sx={{
                                  backgroundColor: 'white',
                                  border: '1px solid #dee2e6',
                                  width: 28,
                                  height: 28,
                                  '&:hover': { 
                                    backgroundColor: '#e9ecef',
                                    transform: 'scale(1.05)'
                                  },
                                  '&:disabled': { 
                                    backgroundColor: '#f8f9fa',
                                    color: '#adb5bd'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <RemoveIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <Typography variant="body1" sx={{
                                fontWeight: 700,
                                fontSize: '1rem',
                                minWidth: 30,
                                textAlign: 'center',
                                color: '#0A2A66',
                                background: 'white',
                                borderRadius: 1,
                                px: 1,
                                py: 0.25,
                                border: '1px solid #dee2e6'
                              }}>
                                {item.quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleQuantityChange(item, item.quantity + 1)}
                                sx={{
                                  backgroundColor: 'white',
                                  border: '1px solid #dee2e6',
                                  width: 28,
                                  height: 28,
                                  '&:hover': { 
                                    backgroundColor: '#e9ecef',
                                    transform: 'scale(1.05)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <AddIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleRemoveItem(item)}
                              sx={{
                                backgroundColor: 'linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%)',
                                border: '1px solid #fed7d7',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: '#fed7d7',
                                  transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
      {cartSummary.find(summary => summary.providerId === providerId)?.services.map((item) => (
                    <Card key={`${item.id}-${item.itemType}-${item.providerId}`} sx={{
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      border: '1px solid #f1f3f4',
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                        borderColor: '#0A2A66'
                      }
                    }}>
                      <CardContent sx={{ p: isMobile ? 1.5 : 2.5 }}>
                        <Box sx={{ display: 'flex', gap: isMobile ? 1.5 : 2.5 }}>
                          {/* Service Image */}
                          <CardMedia
                            component="img"
                            sx={{ 
                              width: isMobile ? 70 : 80, 
                              height: isMobile ? 70 : 80, 
                              borderRadius: 2, 
                              objectFit: 'cover',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              border: '1px solid #f8f9fa',
                              flexShrink: 0
                            }}
                            image={item.image || 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=80&h=80&fit=crop'}
                            alt={item.serviceName || item.name}
                          />
                          <Box sx={{ 
                            flexGrow: 1, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            justifyContent: 'space-between',
                            minWidth: 0,
                            overflow: 'hidden'
                          }}>
                            <Box>
                              <Typography variant="subtitle1" sx={{ 
                                fontSize: isMobile ? '0.85rem' : '1rem',
                                fontWeight: 600,
                                color: '#212529',
                                mb: 0.3,
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}>
                                {item.serviceName || item.name}
                            </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ 
                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                color: '#666',
                                mb: 0.3,
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                by {item.providerName || 'Provider'}
                            </Typography>
                            {/* Duration with Icon */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <TimeIcon sx={{ fontSize: isMobile ? 12 : 14, color: '#0A2A66' }} />
                                <Typography variant="body2" color="text.secondary" sx={{
                                  fontSize: isMobile ? '0.7rem' : '0.8rem',
                                  color: '#6c757d',
                                  fontWeight: 500
                                }}>
                                  {formatDuration(item.duration)}
                              </Typography>
                            </Box>
                              <Chip 
                                label="Service" 
                                size="small" 
                                sx={{ 
                                  background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
                                  color: '#0A2A66',
                                  fontWeight: 600,
                                  fontSize: isMobile ? '0.6rem' : '0.7rem',
                                  height: isMobile ? 16 : 20
                                }} 
                              />
                            </Box>
                            <Typography variant="h6" color="primary" sx={{
                              mt: 0.5,
                              fontWeight: 700,
                              fontSize: isMobile ? '0.95rem' : '1.1rem',
                              color: '#0A2A66'
                            }}>
                              {formatPrice(item.price)}
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: isMobile ? 0.8 : 1.5,
                            justifyContent: 'space-between',
                            minWidth: isMobile ? 50 : 80,
                            flexShrink: 0
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: isMobile ? 0.3 : 1,
                              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                              borderRadius: 1.5,
                              p: isMobile ? 0.3 : 1,
                              border: '1px solid #dee2e6'
                            }}>
                              <IconButton
                                size="small"
                                onClick={() => handleQuantityChange(item, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                                sx={{
                                  backgroundColor: 'white',
                                  border: '1px solid #dee2e6',
                                  width: isMobile ? 22 : 28,
                                  height: isMobile ? 22 : 28,
                                  '&:hover': { 
                                    backgroundColor: '#e9ecef',
                                    transform: 'scale(1.05)'
                                  },
                                  '&:disabled': { 
                                    backgroundColor: '#f8f9fa',
                                    color: '#adb5bd'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <RemoveIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                              </IconButton>
                              <Typography variant="body1" sx={{
                                fontWeight: 700,
                                fontSize: isMobile ? '0.85rem' : '1rem',
                                minWidth: isMobile ? 20 : 30,
                                textAlign: 'center',
                                color: '#0A2A66',
                                background: 'white',
                                borderRadius: 1,
                                px: isMobile ? 0.5 : 1,
                                py: 0.25,
                                border: '1px solid #dee2e6'
                              }}>
                                {item.quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleQuantityChange(item, item.quantity + 1)}
                                sx={{
                                  backgroundColor: 'white',
                                  border: '1px solid #dee2e6',
                                  width: isMobile ? 22 : 28,
                                  height: isMobile ? 22 : 28,
                                  '&:hover': { 
                                    backgroundColor: '#e9ecef',
                                    transform: 'scale(1.05)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <AddIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                              </IconButton>
                            </Box>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleRemoveItem(item)}
                              sx={{
                                backgroundColor: 'linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%)',
                                border: '1px solid #fed7d7',
                                width: isMobile ? 28 : 32,
                                height: isMobile ? 28 : 32,
                                '&:hover': {
                                  backgroundColor: '#fed7d7',
                                  transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
      {cartSummary.find(summary => summary.providerId === providerId)?.offers && cartSummary.find(summary => summary.providerId === providerId).offers.length > 0 && (
              <Box sx={{ mb: 3, px: 2 }}>
                <Typography variant="h6" sx={{ 
                  mb: 2, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontWeight: 700,
                  color: '#0A2A66',
                  fontSize: '1.1rem',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -4,
                    left: 0,
                    width: 40,
                    height: 2,
                    background: 'linear-gradient(135deg, #0A2A66 0%, #1a365d 100%)',
                    borderRadius: 1
                  }
                }}>
                  <StoreIcon sx={{ fontSize: 20 }} />
            Offers ({cartSummary.find(summary => summary.providerId === providerId)?.offers.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {cartSummary.find(summary => summary.providerId === providerId)?.offers.map((item) => (
                    <Card key={`${item.id}-${item.itemType}-${item.providerId}`} sx={{
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      border: '1px solid #f1f3f4',
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                        borderColor: '#0A2A66'
                      }
                    }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', gap: 2.5 }}>
                          {/* Offer Image */}
                          <CardMedia
                            component="img"
                            sx={{ 
                              width: 80, 
                              height: 80, 
                              borderRadius: 2, 
                              objectFit: 'cover',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              border: '1px solid #f8f9fa'
                            }}
                            image={item.image || 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=80&h=80&fit=crop'}
                            alt={item.serviceName || item.name}
                          />
                          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography variant="subtitle1" sx={{ 
                                fontSize: '1rem',
                                fontWeight: 600,
                                color: '#212529',
                                mb: 0.5,
                                lineHeight: 1.2
                              }}>
                                {item.serviceName || item.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ 
                                fontSize: '0.75rem',
                                color: '#666',
                                mb: 0.5,
                                fontWeight: 500
                              }}>
                                by {item.providerName || 'Provider'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ 
                                mb: 1,
                                fontSize: '0.85rem',
                                color: '#6c757d',
                                lineHeight: 1.3
                              }}>
                                {item.description || item.serviceDescription}
                              </Typography>
                              {/* Duration with Icon */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)',
                                  border: '1px solid #ffb74d'
                                }}>
                                  <TimeIcon sx={{ fontSize: 14, color: '#0A2A66' }} />
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{
                                  fontSize: '0.8rem',
                                  color: '#6c757d',
                                  fontWeight: 500
                                }}>
                                  {formatDuration(item.duration)}
                                </Typography>
                              </Box>
                              <Chip 
                                label="Special Offer" 
                                size="small" 
                                sx={{ 
                                  background: 'linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)',
                                  color: '#0A2A66',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: 20
                                }} 
                              />
                            </Box>
                            <Typography variant="h6" color="primary" sx={{
                              mt: 1,
                              fontWeight: 700,
                              fontSize: '1.1rem',
                              color: '#0A2A66'
                            }}>
                              {formatPrice(item.price)}
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: 1.5,
                            justifyContent: 'space-between',
                            minWidth: 80
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                              borderRadius: 2,
                              p: 1,
                              border: '1px solid #dee2e6'
                            }}>
                              <IconButton
                                size="small"
                                onClick={() => handleQuantityChange(item, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                                sx={{
                                  backgroundColor: 'white',
                                  border: '1px solid #dee2e6',
                                  width: 28,
                                  height: 28,
                                  '&:hover': { 
                                    backgroundColor: '#e9ecef',
                                    transform: 'scale(1.05)'
                                  },
                                  '&:disabled': { 
                                    backgroundColor: '#f8f9fa',
                                    color: '#adb5bd'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <RemoveIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <Typography variant="body1" sx={{
                                fontWeight: 700,
                                fontSize: '1rem',
                                minWidth: 30,
                                textAlign: 'center',
                                color: '#0A2A66',
                                background: 'white',
                                borderRadius: 1,
                                px: 1,
                                py: 0.25,
                                border: '1px solid #dee2e6'
                              }}>
                                {item.quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleQuantityChange(item, item.quantity + 1)}
                                sx={{
                                  backgroundColor: 'white',
                                  border: '1px solid #dee2e6',
                                  width: 28,
                                  height: 28,
                                  '&:hover': { 
                                    backgroundColor: '#e9ecef',
                                    transform: 'scale(1.05)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <AddIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleRemoveItem(item)}
                              sx={{
                                backgroundColor: 'linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%)',
                                border: '1px solid #fed7d7',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: '#fed7d7',
                                  transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}
    </Box>
  );
  };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          margin: isMobile ? 0 : 2,
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '90vh',
          height: isMobile ? '100vh' : 'auto',
          pt: 0,
          pb: 0,
          display: 'flex',
          flexDirection: 'column',
          position: isMobile ? 'fixed' : 'relative',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden' // Prevent content from overflowing
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #0A2A66 0%, #1a365d 100%)',
        color: 'white',
        borderRadius: isMobile ? 0 : '16px 16px 0 0',
        boxShadow: '0 8px 32px rgba(10, 42, 102, 0.3)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        overflow: 'visible',
        p: isMobile ? '16px 16px 20px 16px' : 3,
        pt: isMobile ? 'calc(16px + env(safe-area-inset-top, 0px))' : 3,
        pb: isMobile ? 2.5 : 3,
        flexShrink: 0,
        minHeight: isMobile ? 'auto' : 'inherit'
      }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              position: 'relative',
              zIndex: 2,
              gap: isMobile ? 1 : 2
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? 1.5 : 3,
            flex: 1,
            minWidth: 0,
            pr: isMobile ? 1 : 0
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: isMobile ? 32 : 48,
              height: isMobile ? 32 : 48,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              flexShrink: 0
            }}>
              <CartIcon sx={{ 
                color: 'white', 
                fontSize: isMobile ? 20 : 28 
              }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant={isMobile ? "subtitle1" : "h5"} sx={{ 
                fontWeight: 700,
                fontSize: isMobile ? '1.125rem' : '1.5rem',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                mb: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.3
              }}>
                {totalItems} {totalItems === 1 ? 'item' : 'items'} in cart
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              width: isMobile ? 36 : 40,
              height: isMobile ? 36 : 40,
              flexShrink: 0,
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <CloseIcon sx={{ fontSize: isMobile ? 22 : 24 }} />
          </IconButton>
            </Box>
      </DialogTitle>

      <DialogContent sx={{ 
        p: 0, 
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: '#f5f5f5',
        flex: 1,
        overflow: 'auto',
        minHeight: 0,
        // Ensure proper scrolling on mobile
        WebkitOverflowScrolling: 'touch'
      }}>
        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Cart Content */}
        {!loading && (
          <>
            {totalItems === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Your cart is empty
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add some items to get started
                </Typography>
              </Box>
            ) : (
              <>
                {/* Provider Tabs */}
                {providers.length > 1 && (
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                      value={selectedTab} 
                      onChange={handleTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      {cartSummary.map((summary, index) => (
                        <Tab 
                          key={summary.providerId} 
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{summary.providerName}</span>
                              <Box
                                sx={{
                                  backgroundColor: '#ff6b35',
                                  color: 'white',
                                  borderRadius: '50%',
                                  minWidth: 20,
                                  height: 20,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold'
                                }}
                              >
                                {summary.items.length}
                              </Box>
                            </Box>
                          }
                          sx={{ 
                            textTransform: 'none',
                            fontWeight: 500,
                            minWidth: 'auto',
                            px: 2
                          }}
                        />
                      ))}
                    </Tabs>
                  </Box>
                )}

                {/* Cart Items */}
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  p: 2,
                  // Add bottom padding on mobile to account for fixed DialogActions (~80px) + BottomNav (56px) + extra space (24px) = 160px
                  pb: isMobile ? '160px' : 2,
                  minHeight: 0,
                  WebkitOverflowScrolling: 'touch'
                }}>
                  {(() => {
                    if (selectedProvider) {
                      return renderProviderItems(selectedProvider);
                    } else {
                      return cartSummary.map((summary) => (
                      <Box key={summary.providerId} sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                          {getProviderIcon(summary.providerId)}
                          {summary.providerName}
                        </Typography>
                        {renderProviderItems(summary.providerId)}
                      </Box>
                    ));
                    }
                  })()}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: isMobile ? 2 : 4, 
        gap: isMobile ? 1.5 : 3,
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        borderTop: '1px solid #e9ecef',
        borderRadius: isMobile ? 0 : '0 0 16px 16px',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'stretch',
        flexShrink: 0,
        position: isMobile ? 'fixed' : 'relative',
        bottom: isMobile ? 56 : 'auto', // Position above bottom nav
        left: 0,
        right: 0,
        zIndex: 2,
        pb: isMobile ? 2 : 4,
        boxShadow: isMobile ? '0 -2px 8px rgba(0,0,0,0.1)' : 'none'
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 700,
            px: isMobile ? 3 : 5,
            py: isMobile ? 1.5 : 2,
            fontSize: isMobile ? '1rem' : '1.1rem',
            borderColor: '#0A2A66',
            color: '#0A2A66',
            borderWidth: 2,
            minWidth: isMobile ? '100%' : 180,
            width: isMobile ? '100%' : 'auto',
            '&:hover': {
              borderColor: '#0A2A66',
              backgroundColor: 'rgba(10, 42, 102, 0.08)',
              transform: isMobile ? 'none' : 'translateY(-2px)',
              boxShadow: isMobile ? 'none' : '0 4px 12px rgba(10, 42, 102, 0.2)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          Continue Shopping
        </Button>
        {cartSummary.map((summary, index) => (
          <Button
            key={summary.providerId}
            variant="contained"
            onClick={() => handleCheckout(summary.providerId)}
            sx={{ 
              display: selectedTab === index ? 'block' : 'none',
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 800,
              px: isMobile ? 3 : 5,
              py: isMobile ? 1.5 : 2,
              fontSize: isMobile ? '1rem' : '1.1rem',
              background: 'linear-gradient(135deg, #0A2A66 0%, #1a365d 100%)',
              boxShadow: '0 6px 20px rgba(10, 42, 102, 0.4)',
              minWidth: isMobile ? '100%' : 220,
              width: isMobile ? '100%' : 'auto',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a365d 0%, #0A2A66 100%)',
                transform: isMobile ? 'none' : 'translateY(-2px)',
                boxShadow: isMobile ? '0 6px 20px rgba(10, 42, 102, 0.4)' : '0 8px 24px rgba(10, 42, 102, 0.5)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CartIcon sx={{ fontSize: 20 }} />
              Checkout {summary.providerName} ({formatPrice(summary.total)})
            </Box>
          </Button>
        ))}
      </DialogActions>

      {/* Bottom Navigation - Fixed at bottom on mobile */}
      {isMobile && (
        <Box sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          zIndex: 3 
        }}>
          <BottomNavigation />
        </Box>
      )}

      {/* Sign Up Prompt Modal for Guest Users */}
      <SignUpPromptModal
        open={showSignUpPrompt}
        onClose={() => setShowSignUpPrompt(false)}
        featureName={restrictedFeature}
      />
    </Dialog>
    </>
  );
};

export default Cart; 